import { CheckCircle as CheckIcon, Upload as UploadIcon } from '@mui/icons-material';
import { Box, CircularProgress, List, ListItem, ListItemText, Typography } from '@mui/material';
import React from 'react';
import { Palette } from 'Palette';
import { StyledButton, StyledButtonPanel } from '../../styles/StyledButtons';
import StyledSection from '../../styles/StyledSection';
import { DocumentStatusCard } from './DocumentStatusCard';
import { UseAIAssistedCreationWizardResult } from './useAIAssistedCreationWizard';

type WizardStepsProps = Pick<
  UseAIAssistedCreationWizardResult,
  | 'state'
  | 'dropzone'
  | 'handleDocumentSelection'
  | 'removeFile'
  | 'handleGenerate'
  | 'handleViewContent'
  | 'handleNoViewContent'
>;

export const AIAssistedCreationWizardInitialStep: React.FC<
  Pick<WizardStepsProps, 'state' | 'handleViewContent' | 'handleNoViewContent'>
> = ({ state, handleViewContent, handleNoViewContent }) => (
  <StyledSection transparent sx={{ textAlign: 'center' }}>
    {state.isCheckingDocuments ? (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          py: 4,
        }}
      >
        <CircularProgress size={40} />
        <Typography variant="h6" gutterBottom>
          Preparing...
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Checking for available documents
        </Typography>
      </Box>
    ) : (
      <>
        <Typography variant="h6" gutterBottom>
          This document is in the Documents library
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 1 }}>
          Open the Documents tab and Import to choose a page, a page range, or part of a page.
        </Typography>
        <StyledButtonPanel sx={{ mt: 3 }}>
          <StyledButton onClick={handleViewContent}>View content</StyledButton>
          <StyledButton buttonVariant="inactiveOrCancel" onClick={handleNoViewContent}>
            Done
          </StyledButton>
        </StyledButtonPanel>
      </>
    )}
  </StyledSection>
);

export const AIAssistedCreationWizardUploadStep: React.FC<WizardStepsProps> = ({
  state,
  dropzone,
  handleDocumentSelection,
  removeFile,
  handleGenerate,
}) => {
  const { getRootProps, getInputProps, isDragActive } = dropzone;

  return (
    <StyledSection transparent>
      <Typography variant="h6" gutterBottom>
        Add Documents to Library
      </Typography>

      {state.documents.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Previously uploaded documents:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {state.documents.map((doc) => (
              <DocumentStatusCard
                key={doc.name}
                doc={doc}
                isSelected={state.selectedDocuments.map((d) => d.location).includes(doc.location)}
                onSelectionChange={handleDocumentSelection}
              />
            ))}
          </Box>
        </Box>
      )}

      <Box
        {...getRootProps()}
        sx={{
          border: `2px dashed ${Palette.default.border}`,
          borderRadius: 2,
          p: 3,
          mb: 3,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? Palette.default.activeBg : 'transparent',
          '&:hover': {
            backgroundColor: Palette.default.activeBg,
          },
        }}
      >
        <input {...getInputProps()} />
        <UploadIcon
          sx={{
            fontSize: 48,
            color: Palette.default.disabledText,
            mb: 1,
          }}
        />
        <Typography>
          {isDragActive ? 'Drop files here...' : 'Drag & drop files here, or click to select'}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Supported formats: PDF, DOC, DOCX, TXT
        </Typography>
      </Box>

      {state.filesForUpload.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Files to upload:
          </Typography>
          <List dense>
            {state.filesForUpload.map((file, index) => (
              <ListItem key={index}>
                <ListItemText
                  primary={file.name}
                  secondary={`${(file.size / 1024).toFixed(1)} KB`}
                />
                <StyledButton
                  size="small"
                  buttonVariant="inactiveOrCancel"
                  onClick={() => removeFile(index)}
                >
                  Remove
                </StyledButton>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        These documents will be added to the library — you can use them while building content.
      </Typography>

      {state.error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {state.error}
        </Typography>
      )}

      <StyledButtonPanel>
        <StyledButton
          onClick={handleGenerate}
          disabled={state.isUploading}
          startIcon={state.isUploading && <CircularProgress size={20} />}
        >
          {state.isUploading ? 'Uploading...' : 'Add to Library'}
        </StyledButton>
      </StyledButtonPanel>
    </StyledSection>
  );
};

export const AIAssistedCreationWizardSuccessStep: React.FC<
  Pick<WizardStepsProps, 'handleViewContent' | 'handleNoViewContent'>
> = ({ handleViewContent, handleNoViewContent }) => (
  <StyledSection transparent sx={{ textAlign: 'center' }}>
    <CheckIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
    <Typography variant="h6" gutterBottom>
      Documents Added to Library!
    </Typography>
    <Typography variant="body1" sx={{ mb: 3 }}>
      Start building content now?
    </Typography>
    <StyledButtonPanel>
      <StyledButton onClick={handleViewContent}>Yes, View Content</StyledButton>
      <StyledButton buttonVariant="inactiveOrCancel" onClick={handleNoViewContent}>
        No, Exit
      </StyledButton>
    </StyledButtonPanel>
  </StyledSection>
);

export const AIAssistedCreationWizardSteps: React.FC<
  WizardStepsProps & { step: UseAIAssistedCreationWizardResult['state']['step'] }
> = (props) => {
  switch (props.step) {
    case 'initial':
      return (
        <AIAssistedCreationWizardInitialStep
          state={props.state}
          handleViewContent={props.handleViewContent}
          handleNoViewContent={props.handleNoViewContent}
        />
      );
    case 'uploadAndGenerate':
      return <AIAssistedCreationWizardUploadStep {...props} />;
    case 'success':
      return (
        <AIAssistedCreationWizardSuccessStep
          handleViewContent={props.handleViewContent}
          handleNoViewContent={props.handleNoViewContent}
        />
      );
    default:
      return null;
  }
};
